import { Box, IconButton, Tag } from "@chakra-ui/react";
import { useState, useEffect, useMemo } from "react";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { DebouncedInput } from "../utils/DebouncedInput";
import { NoItemCard } from "../utils/NoItemCard";
import { SkeletonList } from "../utils/SkeletonList";
import { YouthKidCard } from "./YouthKidCard";
import { IoFilterSharp } from "react-icons/io5";
import { IoIosArrowUp, IoMdAddCircle } from "react-icons/io";
import { Gender } from "common";
import { GenderSelector } from "../utils/GenderSelector";
import { TeamSelector } from "../utils/TeamSelector";
import { YearSelector } from "../utils/YearSelector";
import { isParamNumeric } from "../utils/utils";
import { useGetTeamById } from "../team/useGetTeamById";
import { Card } from "../utils/Card";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { useNavigate } from "react-router-dom";
import { PropertyForm } from "./PropertyForm";

interface YouthKidSearchProps {
  selectedYouthKidId?: number | null;
  setSelectedYouthKidId?: (id: number | null) => void;
  manageYouthKidButtons?: boolean;
}

export const YouthKidSearch = ({
  selectedYouthKidId,
  setSelectedYouthKidId,
  manageYouthKidButtons,
}: YouthKidSearchProps) => {
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [gender, setGender] = useState<Gender | null>(null);
  const [teamId, setTeamId] = useState<string>("");
  const [year, setYear] = useState<number | null>(null);
  const [filteredTeam] = useGetTeamById(
    isParamNumeric(teamId) ? Number(teamId) : undefined
  );
  const [properties, setProperties] = useState<{ [name: string]: string }>({});
  const navigate = useNavigate();
  const api = useAPI();
  const { data, state, error, query } = useAPIQuery({
    apiCall: api.youthKidGet,
  });
  useToastOnAPIError({
    error: error,
    title: `Unable to get members`,
  });

  const { data: propertiesData, query: propertiesQuery } = useAPIQuery({
    apiCall: api.youthKidPropertiesGet,
  });

  useEffect(() => {
    propertiesQuery({});
  }, [propertiesQuery]);

  const foundYouthKids = useMemo(
    () =>
      (data?.youthKids ?? []).filter((youthKid) => {
        for (const [name, value] of Object.entries(properties)) {
          if (name.length === 0) {
            continue;
          }
          // Only check property exists
          if (value.length === 0) {
            if (!(name in youthKid.properties)) {
              return false;
            }
            continue;
          }
          // Check value is correct
          if (value !== youthKid.properties[name]) {
            return false;
          }
        }
        return true;
      }),
    [data, properties]
  );

  useEffect(() => {
    query({
      search,
      ...(gender ? { gender } : {}),
      ...(isParamNumeric(teamId) ? { teamId: Number(teamId) } : {}),
      ...(year ? { year } : {}),
    });
    if (setSelectedYouthKidId) {
      setSelectedYouthKidId(null);
    }
  }, [gender, query, search, setSelectedYouthKidId, teamId, year]);
  return (
    <Box>
      <Card display={"flex"} margin={2} padding={4}>
        <DebouncedInput
          time={300}
          setValue={(value) => setSearch(value)}
          placeholder={"Search Members..."}
        />
        <IconButton
          variant={"outline"}
          icon={filtersOpen ? <IoIosArrowUp /> : <IoFilterSharp />}
          aria-label={"filters"}
          marginLeft={2}
          onClick={() => setFiltersOpen(!filtersOpen)}
        />
        {manageYouthKidButtons ? (
          <IconButton
            marginLeft={2}
            onClick={() => navigate("/youth-kid/new")}
            colorScheme={THEME_COLOR_SCHEME}
            variant={"outline"}
            icon={<IoMdAddCircle />}
            aria-label={"create"}
          />
        ) : null}
      </Card>
      {filtersOpen ? (
        <Card padding={4} margin={2} display={"flex"} flexDir={"column"}>
          <Box fontWeight={"bold"}>Filters</Box>
          <GenderSelector
            gender={gender}
            setGender={setGender}
            marginBottom={4}
          />
          <TeamSelector value={teamId} setValue={setTeamId} marginBottom={4} />
          <YearSelector year={year} setYear={setYear} />
          <PropertyForm
            propertyOptions={propertiesData?.properties}
            properties={properties}
            setProperties={setProperties}
            buttonText="Add property filter"
          />
        </Card>
      ) : (
        <Box display={"flex"}>
          {gender ? (
            <Tag borderRadius={"full"} variant={"outline"} margin={1}>
              {gender === "m" ? "Male" : "Female"}
            </Tag>
          ) : null}
          {filteredTeam ? (
            <Tag borderRadius={"full"} variant={"outline"} margin={1}>
              {filteredTeam.name}
            </Tag>
          ) : null}
          {year ? (
            <Tag borderRadius={"full"} variant={"outline"} margin={1}>
              Year {year}
            </Tag>
          ) : null}
        </Box>
      )}
      {state === "success" ? (
        <Box display={"flex"} flexDir={"column"} overflow={"auto"} padding={2}>
          {data ? (
            foundYouthKids.length > 0 ? (
              foundYouthKids.map((youthKid) => (
                <YouthKidCard
                  key={youthKid.id}
                  youthKid={youthKid}
                  selectedYouthKidId={selectedYouthKidId}
                  setSelectedYouthKidId={setSelectedYouthKidId}
                />
              ))
            ) : (
              <NoItemCard
                description="No youth kids found"
                buttonText="Create Youth Kid"
                navigateUrl="/youth-kid/new"
              />
            )
          ) : null}
        </Box>
      ) : (
        <SkeletonList height={20} items={3} />
      )}
    </Box>
  );
};
